import { fetchJsonApi } from "../apiClient";

// Defense in depth: never render a URL that points at the originals store from a list/grid
// field. Overridable per build because the storage path layout may differ by host.
const DEFAULT_ORIGINALS_MARK = "/media-originals/";
export const ORIGINALS_MARK =
  (process.env.REACT_APP_ORIGINALS_PATH_MARK || "").trim().toLowerCase() || DEFAULT_ORIGINALS_MARK;

function firstNonOriginalsUrl(...candidates) {
  for (const u of candidates) {
    if (typeof u !== "string" || !u) continue;
    if (u.toLowerCase().includes(ORIGINALS_MARK)) continue;
    return u;
  }
  return "";
}

/**
 * Grid, cards: use derivative URLs only (`media-derivatives`).
 * Skips fields that still point at `media-originals` (legacy bad rows).
 */
export function thumbnailUrl(img) {
  return firstNonOriginalsUrl(img.low_res_url, img.url);
}

/**
 * Lightbox / expanded view: prefer the larger derivative so wide and panorama
 * frames can fill the viewport without huge letterboxing next to a small raster.
 */
export function lightboxUrl(img) {
  return firstNonOriginalsUrl(img.high_res_url, img.low_res_url, img.url);
}

/**
 * Same as {@link thumbnailUrl}. Originals remain excluded via {@link firstNonOriginalsUrl}.
 */
export function fullImageUrl(img) {
  return thumbnailUrl(img);
}

const highResUrlCache = new Map();
// Signed URLs expire server-side (sas_expiry_minutes, currently 60) — cache well under that so
// a long-lived tab reopening a photo it viewed an hour ago gets a fresh URL, not a 403.
const HIGH_RES_URL_CACHE_TTL_MS = 45 * 60 * 1000;

/**
 * The lightbox zoom original, signed on demand. The public list endpoints no longer eagerly
 * sign `high_res_url` for every item (that was 55 unused SAS signs per home-page load), so
 * this fetches it only for the one image a visitor actually opens, and only once per TTL
 * window (cached by id). Resolves to {@link lightboxUrl}'s low-res fallback on any failure —
 * callers always get a usable URL, never a stall.
 */
export function fetchHighResUrl(img) {
  const fallback = lightboxUrl(img);
  const id = img?.id;
  if (!id) return Promise.resolve(fallback);

  const cached = highResUrlCache.get(id);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const entry = { promise: null, expiresAt: Date.now() + HIGH_RES_URL_CACHE_TTL_MS, resolvedUrl: null };
  entry.promise = fetchJsonApi(`/public/media/${encodeURIComponent(id)}/highres`)
    .then((data) => data?.high_res_url || fallback)
    .catch(() => fallback)
    .then((url) => {
      entry.resolvedUrl = url;
      return url;
    });
  highResUrlCache.set(id, entry);
  return entry.promise;
}

/**
 * Synchronous cache peek: the already-resolved signed URL for `img`, or null if it hasn't
 * resolved yet (or was never requested). Lets a modal opening on a photo it already fetched
 * skip straight to the sharp image instead of flashing the "upgrading" blur every time.
 */
export function peekCachedHighResUrl(img) {
  const id = img?.id;
  if (!id) return null;
  const cached = highResUrlCache.get(id);
  if (!cached || cached.expiresAt <= Date.now()) return null;
  return cached.resolvedUrl ?? null;
}
